import type { PlaybookCriterionNode } from "@repo/app-types/entities/playbook";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { Input } from "@repo/ui/Inputs/input";
import { Textarea, TextareaVariant } from "@repo/ui/Inputs/textarea";
import { Label } from "@repo/ui/Label/label";
import { linkStyle } from "@repo/ui/Link/index";
import { SimpleSelect } from "@repo/ui/Select/simpleSelect";
import { type FC, useState } from "react";
import { twMerge } from "tailwind-merge";
import { FieldDiffView } from "./fieldDiffView.js";

const gradingTypeOptions = [
  { value: "yes_no" as const, label: "Yes/No" },
  { value: "1_5_score" as const, label: "1-5 Score" },
];

export interface CriteriaFormProps {
  criteria: PlaybookCriterionNode;
  liveCriteria?: PlaybookCriterionNode;
  onUpdateCriterion: (updates: Partial<PlaybookCriterionNode>) => void;
  isReadOnly?: boolean;
}

type FieldTab = "edit" | "diff";

export const CriteriaForm: FC<CriteriaFormProps> = ({
  criteria,
  liveCriteria,
  onUpdateCriterion,
  isReadOnly = false,
}) => {
  const [nameTab, setNameTab] = useState<FieldTab>("edit");
  const [gradingContextTab, setGradingContextTab] = useState<FieldTab>("edit");

  const liveName = liveCriteria?.name ?? "";
  const liveGradingContext = liveCriteria?.grading_context ?? "";
  const currentGradingContext = criteria.grading_context ?? "";

  const showNameDiffToggle =
    liveCriteria !== undefined && liveName !== criteria.name;
  const showGradingContextDiffToggle =
    liveCriteria !== undefined && liveGradingContext !== currentGradingContext;

  return (
    <div className="flex min-h-0 flex-1 flex-col space-y-4">
      <div>
        <div className="mb-1 flex min-h-8 items-center justify-between">
          <Label size={Size.SMALL}>Name</Label>
          {showNameDiffToggle ? (
            <Button
              variant={ButtonVariant.Tertiary}
              size={Size.MEDIUM}
              className={twMerge("cursor-pointer pr-0", linkStyle())}
              onClick={() => {
                setNameTab(nameTab === "edit" ? "diff" : "edit");
              }}
            >
              {nameTab === "edit" ? "See changes" : "Edit"}
            </Button>
          ) : null}
        </div>
        {showNameDiffToggle && nameTab === "diff" ? (
          <FieldDiffView savedValue={liveName} editedValue={criteria.name} />
        ) : (
          <Input
            value={criteria.name}
            onChange={(event) => {
              onUpdateCriterion({ name: event.target.value });
            }}
            placeholder="Read compliance disclaimer in the first 2 minutes"
            isDisabled={isReadOnly}
            maxLength={300}
          />
        )}
      </div>

      <div className="flex gap-3">
        <div>
          <SimpleSelect
            label="Grading type"
            options={gradingTypeOptions}
            value={criteria.grading_value_type}
            onChange={(value) => {
              onUpdateCriterion({ grading_value_type: value });
            }}
            isDisabled={isReadOnly}
            placeholder="Select grading type..."
          />
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col">
        <div className="mb-1 flex min-h-8 items-center justify-between">
          <Label size={Size.SMALL}>Grading context</Label>
          {showGradingContextDiffToggle ? (
            <Button
              variant={ButtonVariant.Tertiary}
              size={Size.MEDIUM}
              className={twMerge("cursor-pointer pr-0", linkStyle())}
              onClick={() => {
                setGradingContextTab(
                  gradingContextTab === "edit" ? "diff" : "edit",
                );
              }}
            >
              {gradingContextTab === "edit" ? "See changes" : "Edit"}
            </Button>
          ) : null}
        </div>
        {showGradingContextDiffToggle && gradingContextTab === "diff" ? (
          <FieldDiffView
            className="min-h-0 flex-1 overflow-y-auto"
            savedValue={liveGradingContext}
            editedValue={currentGradingContext}
          />
        ) : (
          <Textarea
            value={currentGradingContext}
            onChange={(value) => {
              onUpdateCriterion({ grading_context: value });
            }}
            placeholder="Add context that the AI grader should use..."
            variant={TextareaVariant.DEFAULT}
            className="w-full"
            fillHeight
            maxLength={11000}
            isDisabled={isReadOnly}
          />
        )}
      </div>
    </div>
  );
};
